<script lang="ts">
  import { slide } from 'svelte/transition';
  import { t } from '$core/i18n';
  import { formatProblemDuration, type SessionStore } from '$features/session';
  import type { PreferencesStore } from '$core/preferences';
  import RatingCard from '$ui/components/RatingCard.svelte';
  import ShortcutHelpModal from '$ui/components/ShortcutHelpModal.svelte';
  import { Button } from '$ui/primitives/Button';
  import { Icon } from '$ui/primitives/Icon';

  let { store, prefs }: { store: SessionStore; prefs: PreferencesStore } = $props();

  // --- Live timer ---
  let liveMs = $state(0);

  $effect(() => {
    if (store.problem.state !== 'running') return;
    const id = window.setInterval(() => {
      const started = store.problem.startedAt;
      liveMs = started === null ? 0 : Date.now() - started;
    }, 100);
    return () => window.clearInterval(id);
  });

  let isCountdown = $derived(store.problem.mode === 'countdown');

  let displayedMs = $derived.by(() => {
    if (store.problem.state === 'running') {
      if (isCountdown) {
        const elapsed = store.problem.startedAt === null ? 0 : liveMs;
        return Math.max(0, store.problem.countdownDurationMs - elapsed);
      }
      return liveMs;
    }
    // Idle o evaluating
    if (isCountdown && store.problem.state === 'idle') {
      return store.problem.countdownDurationMs > 0
        ? store.problem.countdownDurationMs
        : (prefs.prefs.countdownDurationMs || 0);
    }
    return store.problem.elapsedMs;
  });

  let displayText = $derived(
    store.problem.state === 'running' && prefs.prefs.blindMode
      ? t('problemTimer.timePlaceholder')
      : formatProblemDuration(displayedMs)
  );

  let countdownClass = $derived.by(() => {
    if (!isCountdown) return '';
    if (store.problem.state !== 'running') return '';
    const remaining = displayedMs;
    if (remaining <= 10_000) return 'countdown-danger';
    if (remaining <= 60_000) return 'countdown-warning';
    return '';
  });

  let timesUp = $derived(
    isCountdown && store.problem.state === 'evaluating' && displayedMs === 0
  );

  function clampDuration(value: string): number {
    const n = Number(value);
    if (!Number.isFinite(n)) return 3;
    return Math.max(1, Math.min(60, Math.floor(n)));
  }

  function handleModeChange(mode: 'timer' | 'countdown') {
    store.updateProblemTimerSettings(mode, prefs.prefs.countdownDurationMs);
  }

  let localMins = $state(3);
  let localSecs = $state(0);
  let isEditingDuration = $state(false);

  $effect(() => {
    if (isEditingDuration) return;
    const totalMs = prefs.prefs.countdownDurationMs || 180000;
    localMins = Math.floor(totalMs / 60000);
    localSecs = Math.floor((totalMs % 60000) / 1000);
  });

  function commitDuration() {
    let mins = Math.max(0, Math.min(60, Math.floor(Number(localMins) || 0)));
    let secs = Math.max(0, Math.min(59, Math.floor(Number(localSecs) || 0)));
    if (mins === 60) secs = 0;
    const totalMs = Math.max(1000, mins * 60000 + secs * 1000);
    store.updateProblemTimerSettings('countdown', totalMs);
    localMins = mins;
    localSecs = secs;
  }

  function handleDurationKeydown(e: KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      (e.currentTarget as HTMLInputElement).blur();
    }
  }



  // --- Inline conjecture ---
  let conjectureText = $state('');
  let conjectureSaving = $state(false);

  // Conjetura colapsable durante evaluating (F6 §17, F8 §4.4, F9 §2.5).
  // El usuario puede expandirla si quiere guardar un insight DURANTE la
  // clasificación, pero por defecto se oculta para no competir con las
  // decisiones primarias (CORRECT/INCORRECT/UNRESOLVED).
  let conjectureOpen = $state(false);
  let isEvaluating = $derived(store.problem.state === 'evaluating');

  // Descubribilidad de atajos (F10 §1.7 Learnability, F6 §31).
  // La infraestructura de teclas ya existe (SHORTCUTS en keys.ts), pero sin
  // un punto visible el usuario no descubre que están disponibles. El botón
  // flotante abre un modal con la lista completa desde DEFAULT_SHORTCUTS o
  // desde el mapa personalizado que vive en prefs.
  let showShortcuts = $state(false);

  // El `<svelte:window>` handler de la tecla `?` vive en el template más
  // abajo. Aquí solo el helper de persistencia de la conjetura.
  async function saveConjecture() {
    const text = conjectureText.trim();
    if (!text || conjectureSaving) return;
    conjectureSaving = true;
    try {
      await store.saveConjecture(text);
      conjectureText = '';
    } finally {
      conjectureSaving = false;
    }
  }
</script>

<svelte:window
  onkeydown={(e) => {
    if (e.key === '?' && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      showShortcuts = !showShortcuts;
    }
  }}
/>

<section
  class="problem"
  data-state={store.problem.state}
>
  {#if store.macro.state === 'focus_running' || store.macro.state === 'focus_paused'}
    <div class="timer-mode-controls">
      <div class="mode-pill">
        <button
          type="button"
          class="mode-btn"
          class:active={store.problem.mode === 'timer'}
          class:locked={store.problem.state === 'running'}
          onclick={(e) => { e.preventDefault(); e.stopPropagation(); handleModeChange('timer'); }}
          title={t('settings.problemTimerTimer')}
          aria-label={t('settings.problemTimerTimer')}
        >
          <Icon name="timer" size={18} />
        </button>
        <button
          type="button"
          class="mode-btn"
          class:active={store.problem.mode === 'countdown'}
          class:locked={store.problem.state === 'running'}
          onclick={(e) => { e.preventDefault(); e.stopPropagation(); handleModeChange('countdown'); }}
          title={t('settings.problemTimerCountdown')}
          aria-label={t('settings.problemTimerCountdown')}
        >
          <Icon name="hourglass" size={18} />
        </button>

        {#if store.problem.mode === 'countdown'}
          <div
            class="duration-section"
            in:slide={{ axis: 'x', duration: 200 }}
            out:slide={{ axis: 'x', duration: 150 }}
          >
            <div class="duration-inner">
              <span>{t('settings.countdownDuration')}</span>
              <input
                type="number"
                min="0"
                max="60"
                bind:value={localMins}
                disabled={store.problem.state === 'running'}
                oninput={commitDuration}
                onfocus={() => (isEditingDuration = true)}
                onblur={() => (isEditingDuration = false)}
                onkeydown={handleDurationKeydown}
              />
              <span class="colon">:</span>
              <input
                type="number"
                min="0"
                max="59"
                bind:value={localSecs}
                disabled={store.problem.state === 'running'}
                oninput={commitDuration}
                onfocus={() => (isEditingDuration = true)}
                onblur={() => (isEditingDuration = false)}
                onkeydown={handleDurationKeydown}
              />
            </div>
          </div>
        {/if}
      </div>
    </div>
  {/if}

  <div class="timer-zone">
    {#if store.problem.state === 'running' && prefs.prefs.blindMode}
      <div class="clock-placeholder" role="timer" aria-live="off" aria-label="Problem timer running">
        <span class="dots" aria-hidden="true">·  ·  ·</span>
      </div>
    {:else}
      <!-- El clock SIEMPRE vive aquí, mismo slot, misma posición. Al pasar
           a evaluating, el RatingCard aparece DEBAJO sin desplazarlo. -->
      <div
        class="clock {countdownClass}"
        class:times-up={timesUp}
        role="button"
        tabindex="0"
        aria-live="off"
        aria-describedby="problem-hint"
        data-running={store.problem.state === 'running' || undefined}
        onpointerup={(e) => {
          // Solo activar si es un toque (touch) o clic primario, y si no se ha arrastrado
          if (e.pointerType === 'touch' || e.button === 0) {
            store.toggleProblem();
          }
        }}
        onkeydown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            store.toggleProblem();
          }
        }}
        style="cursor: pointer;"
      >
        {timesUp ? t('problemTimer.timesUp') : displayText}
      </div>
      <span id="problem-hint" class="visually-hidden">{t('problemTimer.hintIdle')}</span>
      <button
        type="button"
        class="shortcut-help-btn"
        aria-label={t('shortcuts.openLabel')}
        title={t('shortcuts.openLabel')}
        onclick={() => (showShortcuts = true)}
      >
        <Icon name="keyboard" size={14} />
        <span class="kbd-hint">?</span>
      </button>
      {#if store.problem.state === 'idle' && store.macro.state === 'focus_running'}
        <p class="start-hint" aria-live="polite">Ready when you are — press SPACE</p>
      {/if}
    {/if}

    {#if store.problem.state === 'evaluating'}
      <div class="rating-slot">
        <RatingCard {store} />
      </div>
    {/if}
  </div>

  <div
    class="conjecture-row"
    data-state={store.problem.state}
    data-collapsed={isEvaluating || undefined}
  >
    {#if isEvaluating}
      <button
        type="button"
        class="conj-expand"
        onclick={() => (conjectureOpen = !conjectureOpen)}
        aria-expanded={conjectureOpen}
        aria-label={conjectureOpen ? t('problemTimer.conjectureHide') : t('problemTimer.conjectureShow')}
      >
        <Icon name="lightbulb" size={14} />
        <span>{t('problemTimer.conjectureToggle')}</span>
        <Icon name={conjectureOpen ? 'chevron-up' : 'chevron-down'} size={14} />
      </button>
    {/if}
    {#if !isEvaluating || conjectureOpen}
      <label for="conjecture-inline-input" class="conj-label">
        {t('problemTimer.conjectureLabel')}
        <button
          type="button"
          class="help-icon"
          aria-label={t('problemTimer.conjectureHint')}
          title={t('problemTimer.conjectureHint')}
        >?</button>
      </label>
      <input
        id="conjecture-inline-input"
        type="text"
        class="conj-input"
        placeholder={t('problemTimer.conjecturePlaceholder')}
        spellcheck={false}
        autocomplete="off"
        bind:value={conjectureText}
        onkeydown={(e) => {
          if (e.key === 'Enter') void saveConjecture();
        }}
      />
      <Button
        variant="filled"
        size="sm"
        disabled={!conjectureText.trim() || conjectureSaving}
        loading={conjectureSaving}
        onclick={() => void saveConjecture()}
        ariaLabel={t('problemTimer.conjectureSaveFull')}
      >
        {t('problemTimer.conjectureSave')}
      </Button>
    {/if}
  </div>

  {#if showShortcuts}
    <ShortcutHelpModal
      shortcuts={prefs.prefs.shortcuts}
      onClose={() => (showShortcuts = false)}
    />
  {/if}
</section>

<style>
  /* Sin caja: el timer vive libre. El padding-top ancla el clock en la
     parte superior del área de contenido para que al aparecer el
     RatingCard, el clock se quede en la MISMA posición y solo se añada
     contenido debajo. */
  .problem {
    position: relative;
    background: transparent;
    border: none;
    border-radius: 0;
    padding: var(--space-6) 0 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    min-height: calc(100dvh - 200px);
  }

  /* --- Timer zone --- */
  .timer-mode-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: var(--space-4);
    width: 100%;
    position: relative;
    z-index: 100;
    pointer-events: auto;
  }
  .mode-pill {
    display: inline-flex;
    align-items: center;
    background: var(--color-surface-sunken);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-full);
    padding: 4px;
    height: 44px;
    box-sizing: border-box;
  }
  .mode-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: transparent;
    border: none;
    border-radius: 50%;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all var(--dur-fast) var(--ease-out);
    flex-shrink: 0;
    pointer-events: auto;
  }
  .mode-btn.active {
    background: var(--color-surface);
    color: var(--color-brand);
    box-shadow: var(--shadow-sm);
  }
  .mode-btn:hover:not(.locked) {
    color: var(--color-text-primary);
  }
  .mode-btn.locked {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }
  .duration-section {
    overflow: hidden;
    flex-shrink: 0;
    height: 36px;
  }
  .duration-inner {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 12px 0 4px;
    height: 100%;
  }
  .duration-inner > span:first-child {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-muted);
    white-space: nowrap;
    letter-spacing: 0.04em;
    margin-right: 4px;
  }
  .duration-inner .colon {
    color: var(--color-text-muted);
    font-weight: 700;
    font-family: var(--font-mono);
    font-size: var(--text-sm);
  }
  .duration-inner input {
    width: 36px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    background: var(--color-surface);
    color: var(--color-text-primary);
    text-align: center;
    font-family: var(--font-mono);
    font-size: var(--text-sm);
  }
  .duration-inner input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .clock.countdown-warning {
    color: var(--color-warning);
  }
  .clock.countdown-danger {
    color: var(--color-danger);
  }
  .clock.times-up {
    color: var(--color-danger);
    animation: pulse 1s infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .timer-zone {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: var(--space-4);
    width: 100%;
    /* El clock siempre arranca a la misma altura (≈ 22vh desde arriba).
       Cuando aparece el RatingCard, este se suma debajo en el mismo
       flujo vertical sin empujar al clock. */
    padding-top: clamp(80px, 22vh, 220px);
  }

  .rating-slot {
    width: 100%;
    max-width: 720px;
    margin: var(--space-4) auto 0;
  }

  /* Hint contextual (F6 §33 Nielsen #1 / F6 §31).
     Es el CTA del estado idle — un pulso sutil llama la atención sin gritar. */
  .start-hint {
    margin: 0;
    font-family: var(--font-mono, monospace);
    font-size: 0.8rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    color: var(--color-text-muted);
    text-align: center;
    animation: hint-pulse 2.4s var(--ease-in-out) infinite;
  }

  @keyframes hint-pulse {
    0%, 100% { opacity: 0.55; }
    50% { opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    .start-hint { animation: none; opacity: 0.8; }
  }

  .clock,
  .clock-placeholder {
    font-family: var(--font-mono, monospace);
    font-weight: var(--weight-semibold, 600);
    letter-spacing: -0.03em;
    text-align: center;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    color: var(--color-text-primary);
    /* Choreography: el timer crece con spring al arrancar y se encoge
       al detenerse. Transición suave entre estados — sin saltos secos
       (F5 §2, F6 §42). */
    transition:
      font-size var(--dur-slow) var(--spring-soft),
      color var(--dur-base) var(--ease-out),
      opacity var(--dur-base) var(--ease-out);
  }

  /* Idle: timer chico y atenuado para cederle el foco visual al hint. */
  .problem[data-state='idle'] .clock {
    opacity: 0.7;
    font-size: clamp(3rem, 8vw, 5rem);
  }

  /* Running: el timer crece a tamaño protagonista y toma color brand. */
  .clock[data-running] {
    color: var(--color-brand);
    opacity: 1;
    font-size: clamp(5.5rem, 15vw, 11rem);
  }

  /* Evaluating: mantiene brand + tamaño protagonista. El color refuerza
     continuidad visual — el usuario sigue en la misma sesión, solo cambió
     el modo (F1 §2, F6 §19, F10 §1.1). */
  .problem[data-state='evaluating'] .clock {
    font-size: clamp(5.5rem, 15vw, 11rem);
    color: var(--color-brand);
  }

  .clock-placeholder {
    color: var(--color-text-faint);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* --- Conjecture inline --- */
  .conjecture-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    width: 100%;
    max-width: 720px;
    margin: var(--space-4) auto 0;
  }

  .conj-label {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 0.75rem;
    font-weight: var(--weight-semibold, 600);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-text-muted);
    white-space: nowrap;
  }

  /* Touch target 44x44 (WCAG 2.5.5, F8 §3.7, F10 §3.5). El glifo visual
     queda en 20x20 pero el área clicable es 44x44 con margen negativo para
     no romper el layout del label. */
  .help-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    margin: -12px -12px -12px 0;
    padding: 0;
    border-radius: var(--radius-full);
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    font-family: inherit;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1;
    cursor: help;
    text-transform: none;
    transition: color var(--dur-fast) var(--ease-out);
  }

  .help-icon::before {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid currentColor;
    pointer-events: none;
  }

  .help-icon:hover {
    color: var(--color-brand);
  }

  .help-icon:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  .conj-input {
    flex: 1 1 auto;
    min-width: 0;
    height: 40px;
    padding: 0 var(--space-3);
    font-family: var(--font-body, sans-serif);
    font-size: var(--text-body);
    color: var(--color-text-primary);
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    outline: none;
    transition: border-color var(--dur-fast) var(--ease-out);
  }

  .conj-input:focus-visible {
    border-color: var(--color-accent);
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  /* Expand toggle en modo evaluating (F6 §17 divulgación progresiva). */
  .conj-expand {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    font-family: var(--font-body, sans-serif);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    cursor: pointer;
    transition: color var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out);
  }

  .conj-expand:hover {
    color: var(--color-text-primary);
    border-color: var(--color-text-muted);
  }

  .conj-expand:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  /* Cuando está colapsado el row se reduce al botón expand. */
  .conjecture-row[data-collapsed] {
    justify-content: center;
  }

  .dots {
    font-family: var(--font-mono, monospace);
    font-style: normal;
    font-size: clamp(3rem, 8vw, 5rem);
    letter-spacing: 0.3em;
    color: var(--color-text-faint);
  }

  /* Botón de descubrimiento de atajos (F10 §1.7 Learnability).
     Discreto pero siempre visible; el `?` refuerza la affordance del teclado. */
  .shortcut-help-btn {
    position: fixed;
    bottom: var(--space-4);
    right: var(--space-4);
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: 0 var(--space-3);
    height: 32px;
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-full);
    color: var(--color-text-muted);
    font-family: var(--font-mono, monospace);
    font-size: 0.75rem;
    cursor: pointer;
    z-index: var(--z-raised, 10);
    transition: color var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }

  .shortcut-help-btn:hover {
    color: var(--color-text-primary);
    border-color: var(--color-text-muted);
    background: var(--color-surface-hover);
  }

  .shortcut-help-btn:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  .kbd-hint {
    font-weight: 700;
    letter-spacing: 0.02em;
  }
</style>